import React, { CSSProperties, useMemo, useRef, useCallback, useContext, useEffect, useState } from 'react'
import classnames from 'classnames'

import { VariableSizeList as List } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'

import { groupItems, tsToString } from '../services/TranscriptTransform'
import { TranscriptData, TranscriptItem } from '../data/transcript'
import { useWindowSize } from '../hooks/useWindowSize'
import { PlaybackContext } from './VideoPlayer'

interface TranscriptContext {
  setSize: (index: number, height: number) => boolean
  windowWidth: number
  selectedListIndex: number
}

const TranscriptContext = React.createContext<TranscriptContext>({
  setSize: (i, h) => false,
  windowWidth: 0,
  selectedListIndex: -1,
})

type ItemGroupRowProps = {
  index: number
  style: CSSProperties
  data: TranscriptItem[][]
}

const ItemGroupRow: React.FC<ItemGroupRowProps> = ({index, style, data}) => {
  const { setSize, windowWidth, selectedListIndex } = useContext(TranscriptContext)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSize(index, root.current?.getBoundingClientRect().height || 0)
  }, [ index, windowWidth, setSize ])

  const group = data[index]
  const pronounciationItems = group.filter(i => i.type === 'pronunciation')

  const headItem = pronounciationItems[0]
  if (headItem.type === 'punctuation') return null
  const startTs = parseFloat(headItem.start_time)

  const rest = group.map((item) => {
    let prefix = ''
    if (item !== group[0] && item.type !== 'punctuation') {
      prefix = ' '
    }
    return prefix + item.alternatives[0].content
  }).join('')

  const isCurrent = selectedListIndex === index

  return (
    <div style={style}>
      <div ref={root} className={classnames('d-flex', 'py-1', { 'bg-white': isCurrent })}>
        <dt className="col-3 col-md-2 text-right">
          {tsToString(startTs)}
        </dt>
        <dd className="col-9 col-md-10 mb-0">
          {rest}
        </dd>
      </div>
    </div>
  )
}

interface TranscriptViewProps {
  transcript: TranscriptData
}

type StartStop = [ number, number ]

const TranscriptView: React.FC<TranscriptViewProps> = ({ transcript }) => {
  const sizeMap = useRef<{[key: number]: number}>({})
  const listRef = useRef<List | null>(null)
  const [ selectedListIndex, setSelectedListIndex ] = useState<number>(-1)
  const { currentTime } = useContext(PlaybackContext)

  const setSize = useCallback(
    (index: number, size: number) => {
      if (!(index in sizeMap.current) || (index in sizeMap.current && sizeMap.current[index] !== size)) {
        sizeMap.current = { ...sizeMap.current,  [index]: size }
        listRef.current?.resetAfterIndex(index, true)
        return true // changed
      }
      return false // not changed
    },
    [ listRef ]
  )

  const itemGroups = useMemo(
    () => {
      return groupItems(transcript.items, 0.3, 50)
        .filter(items => {
          const pronounciationLength = items
            .filter(i => i.type === 'pronunciation')
            .length
          return pronounciationLength > 0
        })
    },
    [ transcript ]
  )

  const startStops = useMemo(
    () => {
      return itemGroups.map(group => {
        const pronounciationItems = group.filter(i => i.type === 'pronunciation')
        const headItem = pronounciationItems[0]
        if (headItem.type === 'punctuation') throw new Error('this should never happen')
        const startTs = parseFloat(headItem.start_time)
        const tailItem = pronounciationItems[pronounciationItems.length - 1]
        if (tailItem.type === 'punctuation') throw new Error('this should never happen')
        const endTs = parseFloat(tailItem.end_time)

        return [startTs, endTs] as StartStop
      })
    },
    [ itemGroups ]
  )

  useEffect(
    () => {
      const sslen = startStops.length
      for (let i = 0; i < sslen; i++) {
        const [ start, stop ] = startStops[i]
        if (start < currentTime && currentTime <= stop) {
          setSelectedListIndex(i)
          break
        }
      }
    },
    [ currentTime, startStops, setSelectedListIndex ]
  )

  useEffect(
    () => {
      if (!listRef.current) return
      listRef.current.scrollToItem(selectedListIndex, "center")
    },
    [ selectedListIndex, listRef ]
  )

  const [ windowWidth ] = useWindowSize()

  const getSize = useCallback(
    (index: number) => {
      return sizeMap.current[index] || 56
    },
    [  ]
  )

  return (
    <TranscriptContext.Provider value={{ setSize, windowWidth, selectedListIndex }}>
      <div className="my-4" style={{ height: "80vh", width: "100%" }}>
        <dl style={{ height: "100%", width: "100%" }}>
          <AutoSizer>
            {({ height, width }) => (
              <List
                ref={listRef}
                height={height}
                width={width}
                estimatedItemSize={56}
                itemCount={itemGroups.length}
                itemSize={getSize}
                itemData={itemGroups}
              >
                {ItemGroupRow}
              </List>
            )}
          </AutoSizer>
        </dl>
      </div>
    </TranscriptContext.Provider>
  )
}

export default TranscriptView
